import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { CssBaseline, Box, Typography } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import NavigationBar from './components/layout/NavigationBar';
import Footer from './components/layout/Footer';
import { useScrollTracking } from './hooks/useScrollTracking';
import { useSectionScroll } from './hooks/useSectionScroll';

// Import sections
import HeroSection from './sections/HeroSection';
import VideoSection from './sections/VideoSection';
import TryContactSection from './sections/TryContactSection';
import FeaturesSection from './sections/FeaturesSection'; // Load immediately for navigation

// Import utilities
import { colors } from './utils/colors';
import SchemaOrg from './utils/SchemaOrg';
import features from './config/features';

// Lazy load sections for performance (except frequently navigated ones)
const LazyPricingSection = React.lazy(() => import('./sections/PricingSection'));
const LazyTestimonialsSection = React.lazy(() => import('./sections/TestimonialsSection'));
const LazyFaqSection = React.lazy(() => import('./sections/FaqSection'));

// Error Boundary Component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    // Error caught by ErrorBoundary
  }
  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4">Something went wrong.</Typography>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default function LandingPage() {
  const { t } = useTranslation();
  useScrollTracking();
  useSectionScroll();

  return (
    <ErrorBoundary>
      <CssBaseline />
      <Helmet>
        <title>{t('helmet.title')}</title>
        <meta
          name="description"
          content={t('helmet.description')}
        />
        <link rel="canonical" href="https://ulpian.com" />
        <link rel="manifest" href="/manifest.json" />
        <meta name="theme-color" content={colors.offWhite} />

        {/* Open Graph tags for social sharing */}
        <meta property="og:title" content={t('helmet.og_title')} />
        <meta
          property="og:description"
          content={t('helmet.og_description')}
        />
        <meta property="og:url" content="https://ulpian.com" />
        <meta property="og:site_name" content="Ulpian AI" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://ulpian.com/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t('helmet.og_title')} />
        <meta name="twitter:description" content={t('helmet.og_description')} />
        <meta name="twitter:image" content="https://ulpian.com/og-image.png" />

        {/* Additional SEO meta tags */}
        <meta name="keywords" content="legal AI, legal research, EU law, Croatian law, Austrian law, case law, legal documents, contract analysis, regulatory compliance, legal tech" />
        <meta name="author" content="Ulpian AI" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      <SchemaOrg />

      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: colors.offWhite,
        }}
      >
        <NavigationBar />

        {/* Spacer for fixed navbar */}
        <Box sx={{ height: { xs: '64px', sm: '70px' } }} id="main-content" />

        {features.sections.hero && <HeroSection />}

        {features.sections.features && <FeaturesSection />}

        {features.sections.video && <VideoSection />}

        <Suspense fallback={<div>Loading...</div>}>
          {/* Pricing section controlled by feature toggle in src/config/features.js */}
          {features.sections.pricing && <LazyPricingSection />}
          {features.sections.testimonials && <LazyTestimonialsSection />}
          <LazyFaqSection />
        </Suspense>

        {features.sections.tryContact && <TryContactSection />}
        <Footer />
      </Box>
    </ErrorBoundary>
  );
}
